<template>
  <div>
    <v-dialog
      v-model="dialogOpen"
      :persistent="viewerOpen"
      max-width="900"
      content-class="public-record-dialog"
    >
      <v-card
        v-if="value && record"
        class="public-record-card"
      >
        <header class="public-record-header">
          <div class="public-record-heading">
            <h2 :title="record.name || 'Memory'">
              {{ record.name || 'Memory' }}
            </h2>
            <small
              v-if="ownerName || record.updated_at"
              class="text--secondary"
            >
              {{ [ownerName, memoryDate].filter(Boolean).join(' · ') }}
            </small>
          </div>
          <v-btn
            icon
            aria-label="Close record"
            @click="dialogOpen = false"
          >
            <v-icon>{{ mdiClose }}</v-icon>
          </v-btn>
        </header>
        <div
          class="public-record-body"
          :class="{ 'public-record-body--text': !media.length }"
        >
          <div
            v-if="media.length"
            ref="mediaStage"
            class="public-record-media"
          >
            <button
              v-if="!currentMedia.video && !failedImages[currentMedia.url]"
              type="button"
              class="public-record-image-button"
              aria-label="Open image fullscreen"
              @click="openViewer"
            >
              <img
                :src="currentMedia.url"
                :alt="record.name || 'Memory image'"
                :style="imageStyle(currentMedia, mediaSize)"
                @error="$set(failedImages, currentMedia.url, true)"
              />
            </button>
            <ListVideo
              v-else-if="currentMedia.video"
              :key="currentMedia.url"
              controls
              playsinline
              preload="metadata"
              :poster="videoPosterUrl(currentMedia.url, record)"
              class="public-record-video"
            >
              <source
                :src="videoPreviewUrl(currentMedia.url)"
                :type="currentMedia.mime"
              >
            </ListVideo>
            <a
              v-else
              :href="currentMedia.url"
              target="_blank"
              rel="noopener noreferrer"
              class="public-record-file"
            >Open file</a>
            <template v-if="media.length > 1">
              <v-btn
                icon
                dark
                class="public-record-arrow public-record-arrow--previous"
                aria-label="Previous media"
                @click="moveMedia(-1)"
              >
                <v-icon>{{ mdiChevronLeft }}</v-icon>
              </v-btn>
              <v-btn
                icon
                dark
                class="public-record-arrow public-record-arrow--next"
                aria-label="Next media"
                @click="moveMedia(1)"
              >
                <v-icon>{{ mdiChevronRight }}</v-icon>
              </v-btn>
              <span class="public-record-counter">{{ mediaIndex + 1 }} / {{ media.length }}</span>
            </template>
            <div
              class="public-record-like"
              :class="{ 'public-record-like--video': currentMedia.video }"
            >
              <v-btn
                icon
                dark
                :aria-label="liked ? 'Unlike' : 'Like'"
                :aria-pressed="liked"
                @click.stop="likeRecord"
              >
                <v-icon :color="liked ? 'red' : 'white'">
                  {{ liked ? mdiHeart : mdiHeartOutline }}
                </v-icon>
              </v-btn>
              <button
                v-if="likeCount"
                type="button"
                class="public-record-like-count"
                aria-label="View likes"
                @click.stop="likersOpen = true"
              >
                {{ likeCount }}
              </button>
            </div>
          </div>
          <div class="public-record-information">
            <div
              v-if="record.alternative_text"
              class="public-record-description"
              v-html="record.alternative_text"
            ></div>
            <div
              v-if="!media.length"
              class="pa-4"
            >
              <v-btn
                text
                :aria-pressed="liked"
                @click="likeRecord"
              >
                <v-icon
                  left
                  :color="liked ? 'red' : undefined"
                >
                  {{ liked ? mdiHeart : mdiHeartOutline }}
                </v-icon>
                {{ likeCount || '' }} Like
              </v-btn>
            </div>
            <section
              class="public-record-comments"
              aria-label="Comments"
            >
              <h3 class="public-record-comments-title">
                Comments <span v-if="commentCount">({{ commentCount }})</span>
              </h3>
              <CommentsSection
                v-if="record.id"
                :key="record.id"
                :file-id="record.id"
                fill-height
                @count-updated="commentCount = $event"
              />
              <router-link
                v-if="!authenticated"
                to="/auth"
                class="public-record-signin"
              >
                Sign in to comment
              </router-link>
            </section>
          </div>
        </div>
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="viewerOpen"
      fullscreen
      content-class="public-record-viewer"
    >
      <div
        v-if="viewerOpen"
        class="public-record-viewer-card"
      >
        <header class="public-record-viewer-header">
          <span>{{ viewerIndex + 1 }} / {{ images.length }}</span>
          <v-btn
            icon
            dark
            aria-label="Close fullscreen image"
            @click="viewerOpen = false"
          >
            <v-icon>{{ mdiClose }}</v-icon>
          </v-btn>
        </header>
        <div
          ref="viewerStage"
          v-touch="{ left: () => moveViewer(1), right: () => moveViewer(-1) }"
          class="public-record-viewer-stage"
        >
          <img
            v-if="images[viewerIndex]"
            :src="images[viewerIndex].url"
            :alt="record.name || 'Memory image'"
            :style="imageStyle(images[viewerIndex], viewerSize)"
          />
          <template v-if="images.length > 1">
            <v-btn
              icon
              dark
              class="public-record-arrow public-record-arrow--previous"
              aria-label="Previous image"
              @click="moveViewer(-1)"
            >
              <v-icon>{{ mdiChevronLeft }}</v-icon>
            </v-btn>
            <v-btn
              icon
              dark
              class="public-record-arrow public-record-arrow--next"
              aria-label="Next image"
              @click="moveViewer(1)"
            >
              <v-icon>{{ mdiChevronRight }}</v-icon>
            </v-btn>
          </template>
        </div>
      </div>
    </v-dialog>
    <LikersDialog
      v-if="record"
      v-model="likersOpen"
      :file-id="record.id"
    />
  </div>
</template>

<script>
import {
  mdiClose, mdiChevronLeft, mdiChevronRight, mdiHeart, mdiHeartOutline,
} from '@mdi/js'
import { Touch } from 'vuetify/lib/directives'
import CommentsSection from '@/components/CommentsSection.vue'
import LikersDialog from '@/components/LikersDialog.vue'
import ListVideo from '@/components/ListVideo.vue'
import videoPreviewUrl, { videoPosterUrl } from '@/utils/videoPreview'

export default {
  name: 'PublicRecordDialog',
  components: { CommentsSection, LikersDialog, ListVideo },
  directives: { Touch },
  props: {
    value: { type: Boolean, default: false },
    record: { type: Object, default: null },
    liked: { type: Boolean, default: false },
    likeCount: { type: Number, default: 0 },
    ownerName: { type: String, default: '' },
  },
  data: () => ({
    mdiClose,
    mdiChevronLeft,
    mdiChevronRight,
    mdiHeart,
    mdiHeartOutline,
    mediaIndex: 0,
    viewerIndex: 0,
    viewerOpen: false,
    likersOpen: false,
    commentCount: 0,
    failedImages: {},
    mediaSize: { width: 0, height: 0 },
    viewerSize: { width: 0, height: 0 },
  }),
  computed: {
    dialogOpen: {
      get() { return this.value },
      set(open) { this.$emit('input', open) },
    },
    authenticated() { return !!this.$store.state.jwtToken },
    memoryDate() {
      const date = this.record && this.record.updated_at ? new Date(this.record.updated_at) : null

      return date && !Number.isNaN(date.getTime()) ? date.toLocaleDateString('en-US', {
        year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC',
      }) : ''
    },
    media() {
      if (!this.record) return []
      const urls = Array.isArray(this.record.related_files) && this.record.related_files.length ? this.record.related_files : [this.record.url].filter(Boolean)

      return urls.map((url, sourceIndex) => {
        if (typeof url !== 'string' || !url) return null
        const ext = url.split(/[?#]/)[0].split('.').pop().toLowerCase()
        const video = ['mp4', 'mov', 'm4v', 'webm', 'avi', 'mkv', 'flv', 'wmv', 'mpg', 'mpeg'].includes(ext)
        const rotation = Number((this.record.rotate_position || [])[sourceIndex]) || 0
        const mimeTypes = {
          mov: 'video/quicktime', mkv: 'video/x-matroska', avi: 'video/avi', flv: 'video/x-flv', wmv: 'video/x-ms-wmv', mpg: 'video/mpeg', mpeg: 'video/mpeg', webm: 'video/webm',
        }

        return {
          url, video, rotation, mime: mimeTypes[ext] || 'video/mp4',
        }
      }).filter(Boolean).map((item, index) => ({ ...item, index }))
    },
    currentMedia() { return this.media[this.mediaIndex] || this.media[0] },
    images() { return this.media.filter(item => !item.video && !this.failedImages[item.url]) },
  },
  watch: {
    record: { immediate: true, handler() { this.resetRecord() } },
    value(open) {
      if (open) this.$nextTick(this.observeStages)
      else {
        this.viewerOpen = false
        this.pauseVideos()
        if (this.stageObserver) this.stageObserver.disconnect()
      }
    },
    viewerOpen(open) {
      if (open) {
        window.addEventListener('keydown', this.viewerKeydown, true)
        this.$nextTick(this.observeStages)
      } else {
        window.removeEventListener('keydown', this.viewerKeydown, true)
        if (this.images[this.viewerIndex]) this.mediaIndex = this.images[this.viewerIndex].index
        this.$nextTick(() => {
          const button = this.$refs.mediaStage && this.$refs.mediaStage.querySelector('.public-record-image-button')
          if (button) button.focus()
        })
      }
    },
  },
  beforeDestroy() {
    if (this.stageObserver) this.stageObserver.disconnect()
    window.removeEventListener('keydown', this.viewerKeydown, true)
    this.pauseVideos()
  },
  methods: {
    videoPreviewUrl,
    videoPosterUrl,
    resetRecord() {
      this.viewerOpen = false
      this.likersOpen = false
      this.commentCount = 0
      this.failedImages = {}
      this.mediaIndex = Math.max(0, this.media.findIndex(item => item.url === (this.record && this.record.url)))
      this.$nextTick(this.observeStages)
    },
    observeStages() {
      if (this.stageObserver) this.stageObserver.disconnect()
      if (typeof ResizeObserver === 'undefined') return
      this.stageObserver = new ResizeObserver(entries => {
        entries.forEach(entry => {
          const key = entry.target === this.$refs.mediaStage ? 'mediaSize' : 'viewerSize'
          this[key] = { width: entry.contentRect.width, height: entry.contentRect.height }
        })
      })
      if (this.$refs.mediaStage) this.stageObserver.observe(this.$refs.mediaStage)
      if (this.$refs.viewerStage) this.stageObserver.observe(this.$refs.viewerStage)
    },
    imageStyle(item, size) {
      const quarterTurn = Math.abs(item.rotation % 2) === 1 && size.width && size.height

      return { width: quarterTurn ? `${size.height}px` : '100%', height: quarterTurn ? `${size.width}px` : '100%', transform: `translate(-50%, -50%) rotate(${item.rotation * 90}deg)` }
    },
    pauseVideos() {
      if (this.$refs.mediaStage) this.$refs.mediaStage.querySelectorAll('video').forEach(video => video.pause())
    },
    moveMedia(step) {
      this.pauseVideos()
      this.mediaIndex = (this.mediaIndex + step + this.media.length) % this.media.length
    },
    openViewer() {
      this.viewerIndex = this.images.findIndex(item => item.index === this.currentMedia.index)
      this.viewerOpen = this.viewerIndex >= 0
    },
    moveViewer(step) {
      this.viewerIndex = (this.viewerIndex + step + this.images.length) % this.images.length
    },
    viewerKeydown(event) {
      if (!['Escape', 'ArrowLeft', 'ArrowRight'].includes(event.key)) return
      event.preventDefault()
      event.stopImmediatePropagation()
      if (event.key === 'Escape') this.viewerOpen = false
      else this.moveViewer(event.key === 'ArrowLeft' ? -1 : 1)
    },
    likeRecord() {
      if (this.authenticated) this.$emit('like', this.record)
      else this.$router.push('/auth')
    },
  },
}
</script>

<style>
.v-dialog.public-record-dialog {
  width: calc(100% - 48px);
  height: 80vh;
  height: min(80dvh, 760px);
  max-height: calc(100vh - 48px);
  max-height: calc(100dvh - 48px);
  margin: 24px;
  overflow: hidden;
}
@media (max-width: 600px) {
  .v-dialog.public-record-dialog {
    width: calc(100% - 24px);
    height: 90vh;
    height: 90dvh;
    max-height: calc(100vh - 24px);
    max-height: calc(100dvh - 24px);
    margin: 12px;
  }
}
</style>
<style scoped lang="scss">
.public-record-card { display: flex; flex-direction: column; height: 100%; padding: 16px; overflow: hidden; }
.public-record-header { display: flex; align-items: center; gap: 16px; flex: 0 0 auto; padding: 8px 4px 12px; border-bottom: 1px solid rgba(128,128,128,.2); }
.public-record-heading { flex: 1; min-width: 0; }
.public-record-heading h2 { font-size: 20px; line-height: 1.4; margin: 0; display: -webkit-box; -webkit-box-orient: vertical; -webkit-line-clamp: 2; overflow: hidden; overflow-wrap: anywhere; }
.public-record-body { display: grid; grid-template-columns: minmax(0, 1.15fr) minmax(320px, 1fr); gap: 16px; padding-top: 16px; flex: 1; min-height: 0; }
.public-record-body--text { grid-template-columns: minmax(0, 1fr); }
.public-record-media { position: relative; overflow: hidden; min-height: 0; border-radius: 6px; background: #151515; }
.public-record-image-button { position: absolute; inset: 0; width: 100%; height: 100%; cursor: zoom-in; }
.public-record-image-button:focus-visible { outline: 3px solid #9155fd; outline-offset: -3px; }
.public-record-image-button img, .public-record-viewer-stage img { position: absolute; top: 50%; left: 50%; display: block; max-width: none; max-height: none; margin: 0; object-fit: cover; object-position: center; }
.public-record-video { height: 100%; }
.public-record-video ::v-deep video { height: 100%; max-height: none; min-height: 0; object-fit: contain; }
.public-record-video ::v-deep .list-video__poster { margin: 0; }
.public-record-file { display: grid; height: 100%; place-items: center; color: #fff; }
.public-record-arrow { position: absolute; top: 50%; transform: translateY(-50%); background: rgba(0,0,0,.65); z-index: 2; }
.public-record-arrow--previous { left: 12px; }
.public-record-arrow--next { right: 12px; }
.public-record-counter { position: absolute; top: 16px; right: 16px; padding: 4px 10px; border-radius: 16px; color: #fff; background: rgba(0,0,0,.65); pointer-events: none; }
.public-record-like { position: absolute; bottom: 16px; left: 16px; z-index: 2; display: flex; align-items: center; border-radius: 24px; background: rgba(0,0,0,.7); color: #fff; }
.public-record-like--video { bottom: 64px; }
.public-record-like-count { min-width: 32px; min-height: 44px; padding: 0 12px 0 4px; }
.public-record-information { display: flex; flex-direction: column; min-width: 0; min-height: 0; }
.public-record-description { flex: 0 1 auto; max-height: 45%; padding: 20px; overflow-y: auto; overflow-wrap: anywhere; }
.public-record-description ::v-deep img { max-width: 100%; height: auto; }
.public-record-description ::v-deep video, .public-record-description ::v-deep iframe { max-width: 100%; }
.public-record-comments { display: flex; flex-direction: column; flex: 1; min-height: 160px; border-top: 1px solid rgba(128,128,128,.2); overflow: hidden; }
.public-record-comments-title { flex: 0 0 auto; font-size: 15px; padding: 16px 20px 8px; }
.public-record-signin { flex: 0 0 auto; padding: 12px 20px; }
.public-record-viewer-card { height: 100vh; height: 100dvh; display: flex; flex-direction: column; background: #101010; color: #fff; }
.public-record-viewer-header { display: flex; align-items: center; justify-content: space-between; flex: 0 0 56px; padding: 0 12px 0 20px; }
.public-record-viewer-stage { position: relative; flex: 1; min-height: 0; overflow: hidden; touch-action: pan-y; }
.public-record-viewer-stage img { object-fit: contain; }
@media (max-width: 767px) {
  .public-record-card { padding: 12px; }
  .public-record-header { padding: 4px 0 10px; }
  .public-record-heading h2 { font-size: 18px; }
  .public-record-body { display: flex; flex-direction: column; overflow-y: auto; }
  .public-record-media { flex: 0 0 36vh; flex-basis: 36dvh; min-height: 200px; }
  .public-record-information { flex: 1 0 auto; min-height: 300px; }
  .public-record-description { max-height: none; flex: 0 0 auto; padding: 16px; }
  .public-record-comments { flex: 1 0 240px; }
}
</style>
